import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { AssignSystemToCustomerRequest, AssignSystemToCustomerResponse, GetCustomerSystemsRequest, ListCustomersRequest, ListCustomersResponse, ListUnassignedSystemsQuery, TriggerIngestionRequest, TriggerIngestionResponse } from '@features/customers/domain/customer.types';
import { customerService } from './customerService';

export const customerKeys = {
  all: ['customers'] as const,
  lists: () => [...customerKeys.all, 'list'] as const,
  list: (filters: ListCustomersRequest) => [...customerKeys.lists(), filters] as const,
  details: () => [...customerKeys.all, 'detail'] as const,
  detail: (customerId: string) => [...customerKeys.details(), customerId] as const,
  // Any single `{customerId}` specific queries should be under the detail key hierarchy
  detailSubQueries: {
    systems: (customerId: string, filters: Omit<GetCustomerSystemsRequest, 'customerId'>) => [...customerKeys.detail(customerId), 'systems', filters] as const,
  },
  systemCustomerAssociations: (systemId: string) => [...customerKeys.all, 'system-customer-associations', systemId] as const,
  ingestionHistory: () => [...customerKeys.all, 'ingestion-history'] as const,
  spotlightSearch: (filters: ListCustomersRequest) => [...customerKeys.all, 'spotlight-search', filters] as const,
  unassignedSystems: (filters: ListUnassignedSystemsQuery) => [...customerKeys.all, 'unassigned-systems', filters] as const,
};

export const customerListQueryOptions = (request: ListCustomersRequest = {}) => queryOptions({
  queryKey: customerKeys.list(request),
  queryFn: () => customerService.listCustomers(request),
  placeholderData: keepPreviousData,
  staleTime: 5 * 60 * 1000,
});

export const customerSystemsQueryOptions = (request: GetCustomerSystemsRequest) => {
  const {customerId, ...filters} = request;

  // Provide default values for pagination if not specified
  const requestWithDefaults: GetCustomerSystemsRequest = {
    ...request,
    page: request.page ?? 1,
    pageSize: request.pageSize ?? 50,
  };

  return queryOptions({
    queryKey: customerKeys.detailSubQueries.systems(customerId, filters),
    queryFn: () => customerService.getCustomerSystems(requestWithDefaults),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    enabled: !!customerId,
  });
};

export const systemCustomerAssociationsQueryOptions = (systemId: string) => queryOptions({
  queryKey: customerKeys.systemCustomerAssociations(systemId),
  queryFn: () => customerService.getSystemCustomerAssociations(systemId),
  staleTime: 5 * 60 * 1000,
  enabled: !!systemId,
});

export const ingestionHistoryQueryOptions = (take: number = 10) => queryOptions({
  queryKey: [...customerKeys.ingestionHistory(), take],
  queryFn: () => customerService.getIngestionHistory(take),
});

export function useTriggerIngestionMutation() {
  const queryClient = useQueryClient();

  return useMutation<TriggerIngestionResponse, AxiosError, TriggerIngestionRequest>({
    mutationFn: customerService.triggerExternalDataIngestion,
    onSuccess: () => {
      // Invalidate all ingestion history queries regardless of 'take' parameter
      queryClient.invalidateQueries({
        queryKey: customerKeys.ingestionHistory(),
        exact: false // This ensures all queries starting with this key are invalidated
      });
      // Invalidate customer lists to potentially show updated data
      queryClient.invalidateQueries({queryKey: customerKeys.lists()});
    },
  });
}

export const customerSpotlightSearchQueryOptions = (request: ListCustomersRequest, isEnabled: boolean) => queryOptions({
  queryKey: customerKeys.spotlightSearch(request),
  queryFn: () => customerService.listCustomers(request),
  placeholderData: keepPreviousData,
  enabled: isEnabled,
});

export const unassignedSystemsQueryOptions = (request: ListUnassignedSystemsQuery = {}) => {
  const requestWithDefaults: ListUnassignedSystemsQuery = {
    page: request.page ?? 1,
    pageSize: request.pageSize ?? 20
  };

  return queryOptions({
    queryKey: customerKeys.unassignedSystems(requestWithDefaults),
    queryFn: () => customerService.getUnassignedSystems(requestWithDefaults),
    placeholderData: keepPreviousData, // Prevent layout shift during pagination
  });
};

export function useAssignSystemToCustomerMutation(options?: {
  onSuccess?: (responses: Array<AssignSystemToCustomerResponse>, variables: AssignSystemToCustomerRequest) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation<Array<AssignSystemToCustomerResponse>, AxiosError, AssignSystemToCustomerRequest>({
    mutationFn: customerService.assignSystemToCustomer,
    onSuccess: (responses, variables) => {
      const successful = responses.filter(r => r.success);

      // Invalidate affected queries
      const customerIds = [...new Set(responses.map(r => r.customerId).filter(Boolean))];
      customerIds.forEach(customerId => {
        queryClient.invalidateQueries({
          queryKey: customerKeys.detail(customerId),
          exact: false
        });
      });

      // Invalidate systemCustomerAssociations queries for all assigned systems
      successful.forEach(response => {
        queryClient.invalidateQueries({
          queryKey: customerKeys.systemCustomerAssociations(response.systemId),
        });
      });

      // Invalidate unassigned systems
      queryClient.invalidateQueries({
        queryKey: customerKeys.unassignedSystems({}),
        exact: false
      });

      // Call custom onSuccess if provided
      options?.onSuccess?.(responses, variables);
    },
  });
}

export function useToggleFavoriteCustomerMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: customerService.toggleFavoriteCustomer,
    onMutate: async (customerId) => {
      await queryClient.cancelQueries({queryKey: customerKeys.lists()});

      const previousCustomers = queryClient.getQueriesData<ListCustomersResponse>({
        queryKey: customerKeys.lists()
      });

      queryClient.setQueriesData<ListCustomersResponse>(
        {queryKey: customerKeys.lists()},
        (old) => {
          if (!old) return old;
          return {
            ...old,
            customers: old.customers.map((customer) =>
              customer.customerId === customerId
                ? {...customer, isFavorite: !customer.isFavorite}
                : customer
            ),
          };
        }
      );

      return {previousCustomers};
    },
    onError: (_err, _customerId, context) => {
      context?.previousCustomers.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: customerKeys.lists()});
    },
  });
}

// Query Options
export const customerQueryOptions = (customerId: string) => queryOptions({
  queryKey: customerKeys.detail(customerId),
  queryFn: () => customerService.getCustomerById(customerId),
  staleTime: 5 * 60 * 1000, // 5 minutes
});